import { useHistory } from "react-router";
import { Breakpoints, Button, Icon, type IconName } from "swing-components";

import { AlertError } from "~components";
import {
  DocWorkflowStepQuestionDatum,
  translate as t,
  type DocWorkflowStepData,
  type KeyWithEmptyFnParam,
} from "~utils";

import { createDocumentsTaskUrl, createDocumentsUrl } from "../../../Routes";
import { useAttr } from "../hooks/use-attr";
import { type TaskOnChange } from "../hooks/use-task";
import { getAttributes } from "../utils";
import { Question } from "./question";

export type StepProps = {
  data: DocWorkflowStepData & { state?: DocWorkflowStepQuestionDatum[] };
  task: string;
  onChange: TaskOnChange;
};

export function Step(props: StepProps) {
  const history = useHistory();
  const { data, task, onChange } = props;
  const {
    header,
    questions,
    nextBtn,
    prevBtn,
    gotoPrevStep,
    gotoNextStep,
    isStepBlocked,
    state,
    isTaskComplete,
  } = data;
  const { updateAttributes, isPending, isError } = useAttr({ taskId: task, stepId: gotoNextStep });
  const completedQuestions = getAttributes(state);

  const nextButtonText = isTaskComplete
    ? t("LABEL_RETURN_HOME")
    : nextBtn?.text
      ? t(nextBtn.text as unknown as KeyWithEmptyFnParam)
      : t("LABEL_NEXT");

  const prevButtonText = prevBtn?.text
    ? t(prevBtn.text as unknown as KeyWithEmptyFnParam)
    : t("LABEL_PREVIOUS");

  const showPrev = gotoPrevStep !== -1 && !isTaskComplete;

  function goNextStep() {
    if (isTaskComplete) {
      history.push(createDocumentsUrl());
      return;
    }
    updateAttributes(state);
  }

  function goPrevStep() {
    history.push(createDocumentsTaskUrl(task.toLowerCase(), String(gotoPrevStep)));
  }

  if (isError) {
    return <AlertError />;
  }

  return (
    <div
      css={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        gap: "var(--spacing-6xl)",
      }}
    >
      {header && (
        <header css={{ display: "flex", flexDirection: "column", gap: "var(--spacing-lg)" }}>
          <h3 css={{ margin: 0 }}>{t(header.text as unknown as KeyWithEmptyFnParam)}</h3>
          {header.decorations && (
            <ul
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-lg)",
                paddingLeft: "var(--spacing-lg)",
                margin: 0,
              }}
            >
              {header.decorations.map(({ icon, text }) => (
                <li
                  key={`${header.text}-${text}`}
                  css={{ display: "flex", gap: "var(--spacing-lg)" }}
                >
                  <Icon name={icon as IconName} />
                  <span css={{ fontSize: "var(--swing-font-md)" }}>
                    {t(text as unknown as KeyWithEmptyFnParam)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <hr css={{ width: "100%", borderWidth: 0 }} />
        </header>
      )}

      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-6xl)",
        }}
      >
        {questions.map((question) => (
          <Question
            key={question.attribute}
            {...question}
            state={state?.find((s) => s.attribute === question.attribute)}
            onChange={onChange}
          />
        ))}
      </div>

      <div
        css={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
          gap: "var(--spacing-lg)",
          marginTop: "auto",
          [Breakpoints.mediumDeviceAndUp]: {
            flexDirection: "row-reverse",
            justifyContent: "space-between",
          },
        }}
      >
        <div>
          <Button
            type="primary"
            onClick={goNextStep}
            status={
              isPending
                ? "loading"
                : isStepBlocked
                  ? "disabled"
                  : isTaskComplete || completedQuestions.length === questions.length
                    ? "enabled"
                    : "disabled"
            }
            isFluid
          >
            {nextButtonText}
          </Button>
        </div>
        {showPrev && (
          <div>
            <Button type="secondary" onClick={goPrevStep} isFluid>
              {prevButtonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
